import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Phone, MessageCircle, Mail, Clock, Users, Star, Send } from "lucide-react";
import StickyNavigation from "@/components/layout/StickyNavigation";
import { getPageTitle } from "@/utils/PageUtils";

const ContactUs = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");

  // Tenant-aware prefix for navigation
  const subMatch = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
  const subPrefix = subMatch ? `/site/${subMatch[1]}` : '';
  const tLink = (path: string) => `${subPrefix}${path}`;
  const pageTitle = "Contact Us";

  const navigateToPage = (path: string) => {
    navigate(tLink(path));
    window.scrollTo(0, 0);
  };

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle newsletter subscription
    console.log("Newsletter subscription:", email);
    setEmail("");
    // You could add a toast notification here
  };
  
  useEffect(() => {
    document.title = getPageTitle(pageTitle);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <StickyNavigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {pageTitle}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Please feel free to contact us. We'll be happy to provide you with more information and answer any questions you may have.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {/* Call Us */}
          <Card className="text-center hover:shadow-lg transition-shadow">
            <CardContent className="pt-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Call us</h3>
              <a href="tel:+97145622100" className="text-2xl font-bold text-blue-600 hover:text-blue-700">
                045 622 100
              </a>
            </CardContent>
          </Card>

          {/* WhatsApp */}
          <Card className="text-center hover:shadow-lg transition-shadow">
            <CardContent className="pt-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">WHATSAPP US</h3>
              <a
                href="https://wa.me/971586949645"
                target="_blank"
                rel="noopener noreferrer"
                className="text-2xl font-bold text-green-600 hover:text-green-700"
              >
                +971586949645
              </a>
            </CardContent>
          </Card>

          {/* Business Hours */}
          <Card className="text-center hover:shadow-lg transition-shadow">
            <CardContent className="pt-8">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">We're open from</h3>
              <p className="text-lg font-semibold text-gray-700">8AM till 7PM</p>
              <p className="text-gray-600">Sunday to Friday</p>
            </CardContent>
          </Card>
        </div>

        {/* Group Policies Section */}
        <Card className="mb-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <CardContent className="pt-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Group Policies</h3>
              <p className="text-xl text-blue-100 mb-6">
                Looking for vehicle fleet or group health insurance?
              </p>
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                onClick={() => navigateToPage('/decision-tree')}
              >
                Speak to an expert
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Testimonials */}
        <Card className="mb-16">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl mb-4">What People Say</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Testimonial 1 */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Bought a car insurance from them, they gave me a good offer and helped me with the registration. Definitely recommended."
                </p>
                <p className="font-semibold text-gray-900">Daniel Daniel</p>
              </div>

              {/* Testimonial 2 */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Overall my experience with YallaCompare was outstanding. Their prices were unbeatable, very cooperative and well behaviour staff especially Mr. Haseeb Ahmed. Thank you very much"
                </p>
                <p className="font-semibold text-gray-900">Humayoun Lal</p>
              </div>

              {/* Testimonial 3 */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Received a good competitive discounted rate from Haseeb Ahmed. Appreciate the quick response and proactive commitment to offer the best car insurance rates. Thanks"
                </p>
                <p className="font-semibold text-gray-900">Leon Guedes</p>
              </div>

              {/* Testimonial 4 */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Syed Ahmad Shah always gets the job done for any of all my car insurances. Thank you for the convenience and best rates!"
                </p>
                <p className="font-semibold text-gray-900">Marwan Khalil</p>
              </div>

              {/* Testimonial 5 */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Great experience with Haseeb Ahmed! I was searching for my insurance for a few weeks. He was very professional and helpful! Thanks!"
                </p>
                <p className="font-semibold text-gray-900">Alina Kovalenko</p>
              </div>

              {/* Testimonial 6 */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "The best company, the best prices, trusted them since 2 years for my 2 cars, special Thanks to Syed Ahmad Shah as he is very helpful and will give all the best information in details."
                </p>
                <p className="font-semibold text-gray-900">Marah Al Khawam</p>
              </div>

              {/* Testimonial 7 */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(4)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                  <Star className="h-4 w-4 text-gray-300" />
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Actually they have fast reply and they give several good quotations with good prices. Thanks for Mr.Mohamed Elmadiouni for his fast reply and his assistance for help choosing the best insurance for my car"
                </p>
                <p className="font-semibold text-gray-900">Ahmed Wanis</p>
              </div>

              {/* Testimonial 8 */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Haseeb from Yallacompare is a very prompt and service oriented Sales personal, good work ethics, great service, kept the process easy and flawless!! I would recommend him to anyone looking for vehicle insurance from Yalla compare!! Thank you Haseeb!"
                </p>
                <p className="font-semibold text-gray-900">Thanveer Rehman</p>
              </div>

              {/* Testimonial 9 */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "I've been a Yalla Compare customer for over four years, and I've consistently found their service to be both punctual and competitively priced. I'm grateful for their excellent service, and this time, Mr. Santosh Menon was exceptionally responsive."
                </p>
                <p className="font-semibold text-gray-900">Kalimuthu Ramalingam</p>
              </div>

              {/* Testimonial 10 */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "It was a great experience with your employee Misbah Shaheen. She has a great customer services experience & highly skilled in that. She handled my queries in a very professional way & always available regarding my issues."
                </p>
                <p className="font-semibold text-gray-900">Rehan Hashmi</p>
              </div>

              {/* Testimonial 11 */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Absolutely amazing, special thanks to Usman Ijaz for his great support. What I really appreciate is that he will make sure to provide the required clarity on the prices and the process and he managed to get the policy uploaded to RTA within 40min. Thank you Usman!"
                </p>
                <p className="font-semibold text-gray-900">Nizar Arawi</p>
              </div>

              {/* Testimonial 12 */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Mr. Syed Ahmed Shah is excellent with experience and patience towards clients. Good Work Mr. Shah. Your contribution to Yalla Compare is outstanding."
                </p>
                <p className="font-semibold text-gray-900">Jack Vina</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Newsletter Section */}
        <Card className="mb-16 bg-gradient-to-br from-gray-50 to-blue-50">
          <CardContent className="pt-8">
            <div className="text-center max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold mb-2">Stay in Touch</h3>
              <p className="text-gray-600 mb-6">Subscribe to our newsletter and stay updated.</p>
              
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1"
                />
                <Button type="submit">
                  <Send className="h-4 w-4 mr-2" />
                  Subscribe
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>

        {/* Company Description */}
        <Card className="mb-16">
          <CardContent className="pt-8">
            <div className="text-center">
              <p className="text-lg text-gray-700 font-semibold">
                Yallacompare.com is the leading finance comparison site in the Middle East.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* CTA Section - tenant-aware */}
        <Card className="text-center" style={{ backgroundColor: '#FFFFFF', color: '#111827' }}>
          <CardContent className="pt-8">
            <h3 className="text-3xl font-bold mb-4">Ready to Get Protected?</h3>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Get started today and see how we can help protect what matters most to your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                size="lg"
                className="hover:opacity-90"
                onClick={() => navigateToPage('/decision-tree')}
              >
                Start Decision Matrix
              </Button>
              <Button
                size="lg"
                className="bg-white text-blue-600 border-white hover:bg-gray-50"
                onClick={() => navigateToPage('/')}
              >
                Browse Products
              </Button>
            </div>

            {/* Footer Information */}
            <div className="border-t border-blue-400 pt-8 mt-8">
              <div className="text-sm text-blue-200 space-y-2 max-w-4xl mx-auto">
                <p>
                  Information found on yallacompare is provided for illustrative purposes only, rates are subject to change based on your personal circumstances.
                </p>
                <p>
                  You should always speak to a trained professional before taking out any form of finance.
                </p>
                <p>
                  Implications of non-payment: If you fail to keep up with repayments on your finance, your bank may file a report with the UAE credit rating agency which may impact your ability to borrow from UAE banks in the future.
                </p>
                <p className="font-semibold">
                  YOUR HOME MAY BE REPOSSESSED IF YOU DO NOT KEEP UP REPAYMENTS ON A MORTGAGE, LOAN OR ANY OTHER DEBT SECURED ON IT.
                </p>
                <p>
                  Insurance is provided by Yallacompare Insurance Broker LLC (formerly Gulf Resources Insurance Management Services Limited), an Insurance Broker regulated by the UAE Central Bank (registration #20).
                </p>
                <p className="pt-4">
                  © 2025 yallacompare. All Rights Reserved.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContactUs;
